// wire→domain adapter. Load-bearing:
//   • Frequency is times/years Int on the wire → annualized float in the domain (raw t/y carried too).
//   • source/event/consequence stay enum STRINGS here; mapped to ints at commit.
//   • computedLower/Upper are NOT set here — the provider computes them against the real variables.

import { AiAssistantSessionPartsFragment, AiValidationState } from 'src/graphql/_generated';
import {
  AnalysisCycle,
  FrequencySource,
  ImpactFormula,
  IntelNote,
  RiskIntelSession,
  RiskIntelStatus,
  SuggestedFrequency,
  SuggestedImpact,
  SuggestedRisk,
} from '../../types';
import { formatFrequency, niceFrequencyDisplay, timesYearsToAnnualized } from '../../util/frequency';
import { aiLog } from 'src/utils/logger';

type WireSession = AiAssistantSessionPartsFragment;
type WireCycle = WireSession['riskCycles'][number];
type WireFreqSource = NonNullable<NonNullable<WireSession['frequency']>['industryFrequencyValues']>[number];
type WireImpact = NonNullable<WireSession['impacts']>[number];
type WireImpactSource = NonNullable<WireImpact['sources']>[number];

// `processing` is disambiguated by whether a frequency is chosen: before → frequency rung, after → impacts.
const statusFromWire = (wire: WireSession): RiskIntelStatus => {
  switch (wire.status) {
    case 'active':
      return 'active';
    case 'processing':
      return wire.frequency?.chosenTimes != null ? 'mining_impacts' : 'processing';
    case 'awaiting_frequency':
      return 'awaiting_frequency';
    case 'awaiting_impacts':
      return 'awaiting_impacts';
    // Distinct from awaiting_impacts: quant is done (impacts carry formulas), the accept gate is live.
    case 'quantified':
      return 'quantified';
    case 'completed':
      return 'completed';
    case 'abandoned':
      return 'abandoned';
    case 'errored':
      return 'errored';
    default:
      // An unrecognised server status (new/typo) fails safe to 'errored' (visible + terminal, so the
      // channel tears down) rather than silently re-opening the wizard at 'active' and refetching forever.
      aiLog.warn('riskIntel: unrecognised AI session status', wire.status);
      return 'errored';
  }
};

const validationFromWire = (v: WireCycle['validation']): AnalysisCycle['validation'] => {
  if (!v) return undefined;
  // `state` is the source of truth (the generated AiValidationState enum, used verbatim — no casing
  // conversion); fall back to isValid only when an older parcel presents it null. NB: the fallback
  // can't represent Warning (isValid is true for both Valid and Warning) — a null state loses warnings.
  const state: AiValidationState = v.state ?? (v.isValid ? AiValidationState.Valid : AiValidationState.Invalid);
  return {
    state,
    warningMessage: v.warningMessage ?? undefined,
    reason: v.reason ?? undefined,
    suggestion: v.suggestion ?? undefined,
  };
};

const riskFromWire = (r: WireCycle['risk']): SuggestedRisk | undefined => {
  if (!r) return undefined;
  return {
    name: r.name ?? '',
    source: r.source ?? '', // enum string → int at commit
    event: r.event ?? '',
    consequence: r.consequence ?? '',
    narrative: r.narrative ?? '',
  };
};

const opaqueNote = (text?: string | null): IntelNote => ({ basisOfEstimate: text ?? '', supportingResearch: [] });

const freqSourceFromWire = (s: WireFreqSource, i: number): FrequencySource => {
  const value = s.dataPoint ?? 0;
  return {
    id: `freq-src-${i}`,
    type: 'website',
    title: s.sourceName ?? '',
    industry: s.industry ?? undefined,
    confidence: Math.round((s.confidence ?? 0) * 100), // wire 0..1 → Confidence %
    quote: s.sourceQuote ?? undefined,
    value,
    // A source with no data point shows "No data" rather than a misleading "0 events".
    display: s.dataPoint != null ? niceFrequencyDisplay(s.dataPoint) : 'No data',
  };
};

const frequencyFromWire = (f: WireSession['frequency']): SuggestedFrequency | undefined => {
  if (!f) return undefined;
  const suggestedValue =
    f.suggestedTimes != null && f.suggestedYears ? timesYearsToAnnualized(f.suggestedTimes, f.suggestedYears) : 0;
  const chosenValue =
    f.chosenTimes != null && f.chosenYears ? timesYearsToAnnualized(f.chosenTimes, f.chosenYears) : undefined;
  return {
    suggestedValue,
    display: formatFrequency(suggestedValue),
    note: opaqueNote(f.narrative),
    sources: (f.industryFrequencyValues ?? []).map(freqSourceFromWire),
    chosenValue,
    chosenTimes: f.chosenTimes ?? undefined,
    chosenYears: f.chosenYears ?? undefined,
    // FE-only; default to 'triangulated' on any chosen value so the pill/gate survive refetch (service overrides with the real card).
    chosenSourceId: chosenValue != null ? 'triangulated' : undefined,
  };
};

const impactSourceFromWire = (s: WireImpactSource, i: number): FrequencySource => ({
  id: `impact-src-${i}`,
  type: 'website',
  title: s.sourceName ?? '',
  industry: s.industry ?? undefined,
  confidence: 0,
  weight: Math.round((s.weight ?? 0) * 100), // wire 0..1 → Weight %
  quote: s.sourceQuote ?? undefined,
  value: s.dataPoint ?? 0,
  display: '',
});

const impactFromWire = (im: WireImpact): SuggestedImpact => {
  const hasFormula = im.lowerFormula != null || im.upperFormula != null;
  const formula: ImpactFormula | undefined = hasFormula
    ? {
        lowerRaw: im.lowerFormula ?? '',
        upperRaw: im.upperFormula ?? '',
        note: opaqueNote(im.note),
        suggestedVariables: [],
        sources: (im.sources ?? []).map(impactSourceFromWire),
      }
    : undefined;
  return {
    id: im.id,
    name: im.name ?? '',
    description: im.description ?? '',
    selected: im.selected,
    formula,
  };
};

export const graphqlSessionToDomain = (wire: WireSession): RiskIntelSession => {
  // TEMP (BE handoff evidence): impact id/selected/hasFormula on every session map. The list must stay
  // STABLE across the quant — if the ids or selected flags differ between awaiting_impacts and
  // quantified, or a formula lands on a selected=false impact, the BE is replacing/mis-flagging impacts
  // instead of quanting the submitted subset of the existing list. Remove once the BE bug is confirmed.
  aiLog.info(
    'riskIntel: wire impacts',
    { status: wire.status },
    (wire.impacts ?? []).map((im) => ({
      id: im.id,
      name: im.name,
      selected: im.selected,
      hasFormula: im.lowerFormula != null || im.upperFormula != null,
    })),
  );
  return {
    id: wire.uxId,
    status: statusFromWire(wire),
    assessmentId: wire.assessmentId ?? null,
    cycles: wire.riskCycles.map((c) => ({
      id: c.cycleId,
      userInput: c.userInput,
      createdAt: c.createdAt ?? '',
      validation: validationFromWire(c.validation),
      risk: riskFromWire(c.risk),
    })),
    selectedCycleId: wire.selectedCycleId ?? undefined,
    frequency: frequencyFromWire(wire.frequency),
    impacts: wire.impacts?.map(impactFromWire),
  };
};
